// RelationEditor.tsx
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  useSensor,
  useSensors,
  TouchSensor,
  MouseSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dimension, Relation, RelationTarget, SyntaxchecksData } from "./AppTypes";
import { notifyProjectActivity } from "./api/pingClient";
import { getBaseColor, Mode } from "./colorUtils";

// Muss mit backend/settings.py SPECIAL_CHARACTERS synchron bleiben
const SPECIAL_CHARACTERS = ["·", ";", "\n", "|"];
const stripSpecialChars = (v: string) =>
  SPECIAL_CHARACTERS.reduce((s, c) => s.split(c).join(""), v);

/* ---------- externe Props ---------- */
/**
 * RelationEditor verwaltet die Liste aller Relations:
 * - jede Relation hat Name, Typ (connection/disconnection) und Targets
 * - Targets verweisen auf Dimensionen (mit Modus: all/tags/points)
 */
type Props = {
  relations: Relation[];
  onChange: (rels: Relation[]) => void;
  dimensions: Dimension[];
  allNames: string[]; // für Duplikatsprüfung von Namen
  isActive: boolean;  // ob die Section aufgeklappt ist
  toggle: () => void; // Section auf-/zuklappen
  userHue: number;    // Farbton für User
  mode: Mode;         // light/dark
  projectId: number;  // für Activity-Tracking
  syntaxData?: SyntaxchecksData | null;
};

/* ---- flat item type for the drop zone ---- */
type FlatItem = {
  type: "tag" | "point" | "dimension";
  id: string;
  name: string;
  dimId: string;
};

/** Build flat item list from targets (stable order) */
function buildFlatItems(targets: RelationTarget[], dimensions: Dimension[]): FlatItem[] {
  const items: FlatItem[] = [];
  targets.forEach((target) => {
    const dim = dimensions.find(d => d.id === target.dimensionId);
    if (!dim) return;

    if (target.mode === "all") {
      if (!items.some(x => x.type === "dimension" && x.dimId === dim.id)) {
        items.push({ type: "dimension", id: dim.id, name: dim.name, dimId: dim.id });
      }
    } else {
      target.tagIds?.forEach(tagId => {
        const tag = dim.tags.find(t => t.id === tagId);
        if (tag && !items.some(x => x.type === "tag" && x.id === tag.id)) {
          items.push({ type: "tag", id: tag.id, name: tag.name, dimId: dim.id });
        }
      });
      target.pointIds?.forEach(pointId => {
        const point = dim.points.find(p => p.id === pointId);
        if (point && !items.some(x => x.type === "point" && x.id === point.id)) {
          items.push({ type: "point", id: point.id, name: point.name, dimId: dim.id });
        }
      });
    }
  });
  return items;
}

/**
 * Rebuild targets from flat item list (preserving new order).
 * Groups consecutive items by dimension.
 */
function rebuildTargets(items: FlatItem[]): RelationTarget[] {
  const targets: RelationTarget[] = [];
  items.forEach((item) => {
    if (item.type === "dimension") {
      targets.push({
        dimensionId: item.dimId,
        mode: "all",
        tagIds: [],
        pointIds: [],
      });
      return;
    }
    // Find or create a non-all target for this dimension
    let existing = targets.find(t => t.dimensionId === item.dimId && t.mode !== "all");
    if (!existing) {
      existing = { dimensionId: item.dimId, mode: "points", tagIds: [], pointIds: [] };
      targets.push(existing);
    }
    if (item.type === "tag" && !existing.tagIds.includes(item.id)) {
      existing.tagIds.push(item.id);
      if (existing.pointIds.length === 0) existing.mode = "tags";
      else existing.mode = "points";
    } else if (item.type === "point" && !existing.pointIds.includes(item.id)) {
      existing.pointIds.push(item.id);
      existing.mode = "points";
    }
  });
  return targets;
}

function pickDropEffect(effectAllowed: string | undefined): "copy" | "move" {
  // Sidebar zieht Tags/Points mit effectAllowed="copy"; Dimensionen oft mit "copyMove".
  // Wenn wir hier fälschlich "move" setzen, lehnt der Browser den Drop ab.
  if (!effectAllowed) return "copy";
  const allowed = effectAllowed.toLowerCase();
  if (allowed === "copy") return "copy";
  if (allowed.includes("copy")) return "copy";
  return "move";
}

/* ---------- Sortable Relation Card ---------- */
function SortableRelationCard({
  relation,
  expandedId,
  setExpandedId,
  onUpdate,
  onDelete,
  dimensions,
  userHue,
  mode,
  allNames,
  syntaxStat,
}: {
  key?: React.Key;
  relation: Relation;
  expandedId: string | null;
  setExpandedId: (id: string | null) => void;
  onUpdate: (id: string, updates: any) => void;
  onDelete: (id: string) => void;
  dimensions: Dimension[];
  userHue: number;
  mode: Mode;
  allNames: string[];
  syntaxStat?: { syntax?: string; SyntaxMessages?: string[] };
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: relation.id });

  const cardStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: getBaseColor(4, relation.type, userHue, mode),
  };

  const expanded = expandedId === relation.id;
  const isDuplicate = allNames.filter((n) => n === relation.name).length > 1;
  const [isEditingName, setIsEditingName] = useState(false);
  const [specialCharWarning, setSpecialCharWarning] = useState<string | null>(null);
  const lastDropRef = useRef(0);

  const backendErrors = syntaxStat?.SyntaxMessages;
  const hasBackendError = backendErrors && backendErrors.length > 0;
  const errorMessage = hasBackendError
    ? (Array.isArray(backendErrors) ? backendErrors.map(e => String(e)).join(", ") : String(backendErrors))
    : null;
  const isValid = !errorMessage;
  const validationIcon = !isValid && errorMessage && /warning/i.test(errorMessage) ? "❗️" : "⚠️";

  /* ---- flat items ---- */
  const allItems = buildFlatItems(relation.targets, dimensions);

  /* ---- reorder: move dragged item to position of target item ---- */
  const reorderItem = (dragged: FlatItem, target: FlatItem) => {
    const dragIdx = allItems.findIndex(
      (i) => i.type === dragged.type && i.id === dragged.id
    );
    const targetIdx = allItems.findIndex(
      (i) => i.type === target.type && i.id === target.id
    );
    if (dragIdx === -1 || targetIdx === -1 || dragIdx === targetIdx) return;
    const reordered = [...allItems];
    if (dragged.dimId !== target.dimId) {
      const draggedItems = reordered.filter((i) => i.dimId === dragged.dimId);
      const remaining = reordered.filter((i) => i.dimId !== dragged.dimId);
      const removedBeforeTarget = reordered
        .slice(0, targetIdx)
        .filter((i) => i.dimId === dragged.dimId).length;
      const insertIdx = Math.max(0, targetIdx - removedBeforeTarget);
      remaining.splice(insertIdx, 0, ...draggedItems);
      onUpdate(relation.id, { targets: rebuildTargets(remaining) });
      return;
    }

    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(targetIdx, 0, moved);
    onUpdate(relation.id, { targets: rebuildTargets(reordered) });
  };

  /* ---- remove helpers ---- */
  const removeTag = (tagId: string) => {
    onUpdate(relation.id, {
      targets: (prev: RelationTarget[]) =>
        prev.map((t) => {
          if (t.tagIds?.includes(tagId)) {
            return { ...t, tagIds: t.tagIds.filter((id) => id !== tagId) };
          }
          if (t.mode === "all") {
            const dim = dimensions.find(d => d.id === t.dimensionId);
            if (!dim) return t;
            return {
              ...t,
              mode: "points" as const,
              tagIds: dim.tags.map(tag => tag.id).filter(id => id !== tagId),
              pointIds: dim.points.map(point => point.id),
            };
          }
          return t;
        }).filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all"),
    });
  };

  const removePoint = (pointId: string) => {
    onUpdate(relation.id, {
      targets: (prev: RelationTarget[]) =>
        prev.map((t) => {
          if (t.pointIds?.includes(pointId)) {
            return { ...t, pointIds: t.pointIds.filter((id) => id !== pointId) };
          }
          if (t.mode === "all") {
            const dim = dimensions.find(d => d.id === t.dimensionId);
            if (!dim) return t;
            return {
              ...t,
              mode: "points" as const,
              tagIds: dim.tags.map(tag => tag.id),
              pointIds: dim.points.map(point => point.id).filter(id => id !== pointId),
            };
          }
          return t;
        }).filter(t => t.pointIds.length > 0 || t.tagIds.length > 0 || t.mode === "all"),
    });
  };

  const removeDimension = (dimId: string) => {
    onUpdate(relation.id, {
      targets: (prev: RelationTarget[]) =>
        prev.filter(t => t.dimensionId !== dimId),
    });
  };

  const removeItem = (item: FlatItem) => {
    if (item.type === "tag") removeTag(item.id);
    else if (item.type === "point") removePoint(item.id);
    else if (item.type === "dimension") removeDimension(item.dimId);
  };

  /* ---- add helpers ---- */
  const addToTarget = (dimId: string, type: "tag" | "point", id: string) => {
    const existingIdx = relation.targets.findIndex(t => t.dimensionId === dimId);

    if (existingIdx !== -1) {
      const t = relation.targets[existingIdx];
      // Gewünschte Semantik:
      // - Drop Point => ersetzt Dimension+Tags (mode points, tagIds=[]), weitere Points werden angehängt.
      // - Drop Tag   => ersetzt Dimension+Points (mode tags, pointIds=[]), weitere Tags werden angehängt.
      // - Drop Dimension bleibt separat (addDimension).

      if (type === "point") {
        onUpdate(relation.id, {
          targets: relation.targets.map((target, i) => {
            if (i !== existingIdx) return target;
            const nextPoints = (target.mode === "points" ? target.pointIds : []).includes(id)
              ? (target.mode === "points" ? target.pointIds : [])
              : [...(target.mode === "points" ? target.pointIds : []), id];

            return {
              ...target,
              mode: "points" as const,
              pointIds: nextPoints,
              tagIds: [] as string[],
            };
          }),
        });
        return;
      }

      // type === "tag"
      onUpdate(relation.id, {
        targets: relation.targets.map((target, i) => {
          if (i !== existingIdx) return target;
          const nextTags = (target.mode === "tags" ? target.tagIds : []).includes(id)
            ? (target.mode === "tags" ? target.tagIds : [])
            : [...(target.mode === "tags" ? target.tagIds : []), id];

          return {
            ...target,
            mode: "tags" as const,
            tagIds: nextTags,
            pointIds: [] as string[],
          };
        }),
      });
    } else {
      onUpdate(relation.id, {
        targets: [...relation.targets, {
          dimensionId: dimId,
          mode: type === "tag" ? "tags" : "points",
          tagIds: type === "tag" ? [id] : [],
          pointIds: type === "point" ? [id] : [],
        }]
      });
    }
  };

  const addDimension = (dimId: string) => {
    const exists = relation.targets.some(t => t.dimensionId === dimId);
    if (!exists) {
      onUpdate(relation.id, {
        targets: [...relation.targets, {
          dimensionId: dimId,
          mode: "all" as const,
          tagIds: [],
          pointIds: [],
        }]
      });
      return;
    }

    // Wenn die Dimension schon existiert, dann setzen wir sie auf "all" und löschen
    // explizite Tags/Points (Replace-Semantik).
    onUpdate(relation.id, {
      targets: relation.targets.map((t) => {
        if (t.dimensionId !== dimId) return t;
        return {
          ...t,
          mode: "all" as const,
          tagIds: [],
          pointIds: [],
        };
      }),
    });
  };

  /** Unique key for an item */
  const itemKey = (item: FlatItem) => `${item.type}-${item.id}`;

  /* ---- button style ---- */
  const buttonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    background: mode === "dark"
      ? "rgba(255,255,255,0.08)"
      : "rgba(0,0,0,0.06)",
    borderRadius: 14,
    fontSize: "14px",
    fontWeight: 500,
    color: mode === "dark" ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.75)",
    cursor: "grab",
    userSelect: "none",
    transition: "all 0.15s ease",
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...cardStyle,
        margin: "8px 0",
        padding: 8,
        borderRadius: 6,
        color: "inherit",
      }}
      onDragOver={(e) => {
        if (!expanded) {
          e.stopPropagation();
          return;
        }
        if (
          e.dataTransfer.types.includes("application/x-zeitgeist") ||
          e.dataTransfer.types.includes("application/x-zeitgeist-delete")
        ) {
          e.preventDefault();
          e.dataTransfer.dropEffect = pickDropEffect(e.dataTransfer.effectAllowed);
        }
      }}
      onDrop={(e) => {
        if (!expanded) {
          e.stopPropagation();
          return;
        }
        if (e.dataTransfer.types.includes("application/x-zeitgeist-delete")) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const data = e.dataTransfer.getData("application/x-zeitgeist");
        if (!data) return;

        try {
          const payload = JSON.parse(data);
          if (payload.kind === "create-condition-from-dimension-point") {
            e.preventDefault();
            e.stopPropagation();
            addToTarget(payload.dimensionId, "point", payload.pointId);
          } else if (payload.kind === "create-condition-from-dimension-tag") {
            e.preventDefault();
            e.stopPropagation();
            addToTarget(payload.dimensionId, "tag", payload.tagId);
          } else if (payload.kind === "create-condition-from-dimension") {
            e.preventDefault();
            e.stopPropagation();
            addDimension(payload.dimensionId);
          }
        } catch (err) { console.error(err); }
      }}
      {...attributes}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <div {...listeners} style={{ cursor: "grab" }} title="Drag to reorder · Drag out to remove">☰</div>

        <span
          style={{ cursor: "pointer", padding: "0 6px", userSelect: "none" }}
          onClick={() => setExpandedId(expanded ? null : relation.id)}
        >
          {expanded ? "▾" : "▸"}
        </span>

        {isEditingName ? (
          <>
            <input
              type="text"
              value={relation.name}
              onChange={(e) => {
                const raw = e.target.value;
                const stripped = stripSpecialChars(raw);
                if (stripped !== raw) {
                  const found = SPECIAL_CHARACTERS
                    .filter(c => raw.includes(c))
                    .map(c => c === "\n" ? "\u21b5" : c)
                    .join(", ");
                  setSpecialCharWarning(found);
                  setTimeout(() => setSpecialCharWarning(null), 2000);
                }
                onUpdate(relation.id, { name: stripped });
              }}
              onBlur={() => setIsEditingName(false)}
              onKeyDown={(e) => { if (e.key === "Enter") setIsEditingName(false); }}
              autoFocus
              onFocus={(e) => e.target.select()}
              className={`dimension-card__input ${isDuplicate ? "dimension-card__input--duplicate" : ""}`}
              style={{ flex: 1 }}
            />
            {specialCharWarning && (
              <span style={{ color: "#e55", fontSize: "0.75em", whiteSpace: "nowrap" }}>
                "{specialCharWarning}" nicht erlaubt
              </span>
            )}
          </>
        ) : (
          <div
            style={{ flex: 1, userSelect: "none" }}
            onDoubleClick={() => setIsEditingName(true)}
          >
            {relation.name}
          </div>
        )}

        {/* Syntax Validation Warning */}
        {errorMessage && (
          <span
            title={errorMessage}
            style={{ fontSize: "1.2em", cursor: "help", animation: "pulse 2s infinite" }}
          >
            {validationIcon}
          </span>
        )}

        {/* Relation löschen – entfernt per Drag-out */}
      </div>

      {isDuplicate && (
        <div style={{ color: "#d66", fontSize: "0.8em", marginTop: 4 }}>
          Name already in use
        </div>
      )}

      {expanded && (
        <div style={{ marginTop: 8 }}>
          {/* Type selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <label style={{ fontWeight: 500, fontSize: "0.95em" }}>Type:</label>
            <select
              value={relation.type}
              onChange={(e) => onUpdate(relation.id, { type: e.target.value as any })}
              style={{
                flex: 1,
                padding: "6px 10px",
                borderRadius: 6,
                border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)",
                background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)",
                color: "inherit",
                fontSize: "0.95em"
              }}
            >
              <option value="connection">Connection</option>
              <option value="disconnection">Disconnection</option>
            </select>
          </div>

          {/* Scope + Mode selector */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <label style={{ fontWeight: 500, fontSize: "0.95em" }}>Scope:</label>
            <select
              value={(relation as any).scope || "always"}
              onChange={(e) => onUpdate(relation.id, { scope: e.target.value as any })}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)",
                background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)",
                color: "inherit",
                fontSize: "0.95em"
              }}
            >
              <option value="always">always</option>
              <option value="template">template</option>
              <option value="period">period</option>
              <option value="day">day</option>
              <option value="repair">repair</option>
            </select>
            {((relation as any).scope && (relation as any).scope !== "always") && (
              <>
                <label style={{ fontWeight: 500, fontSize: "0.95em" }}>Mode:</label>
                <select
                  value={(relation as any).mode || "any"}
                  onChange={(e) => onUpdate(relation.id, { mode: e.target.value as any })}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: mode === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.1)",
                    background: mode === "dark" ? "rgba(0,0,0,0.2)" : "rgba(255,255,255,0.8)",
                    color: "inherit",
                    fontSize: "0.95em"
                  }}
                >
                  <option value="any">any</option>
                  <option value="shortterm">shortterm</option>
                  <option value="longterm">longterm</option>
                </select>
              </>
            )}
          </div>

          {/* Points drop zone – purely native HTML DnD for buttons */}
          <div
            onDragOver={(e) => {
              if (
                e.dataTransfer.types.includes("application/x-zeitgeist") ||
                e.dataTransfer.types.includes("application/x-relation-delete")
              ) {
                e.preventDefault();
                e.currentTarget.style.borderColor = "#4ade80";
                e.currentTarget.style.background = mode === "dark"
                  ? "rgba(74, 222, 128, 0.12)"
                  : "rgba(74, 222, 128, 0.08)";
              }
            }}
            onDragLeave={(e) => {
              e.currentTarget.style.borderColor = mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.08)";
              e.currentTarget.style.background = mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.015)";
            }}
            onDrop={(e) => {
              // Reset visual
              e.currentTarget.style.borderColor = mode === "dark"
                ? "rgba(255,255,255,0.1)"
                : "rgba(0,0,0,0.08)";
              e.currentTarget.style.background = mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.015)";

              // Internal reorder drop – item dropped back inside zone = cancel delete
              if (e.dataTransfer.types.includes("application/x-relation-delete")) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              // External drop from sidebar
              const data = e.dataTransfer.getData("application/x-zeitgeist");
              if (!data) return;

              e.preventDefault();
              e.stopPropagation();

              try {
                const payload = JSON.parse(data);
                if (payload.kind === "create-condition-from-dimension-point") {
                  addToTarget(payload.dimensionId, "point", payload.pointId);
                } else if (payload.kind === "create-condition-from-dimension-tag") {
                  addToTarget(payload.dimensionId, "tag", payload.tagId);
                } else if (payload.kind === "create-condition-from-dimension") {
                  addDimension(payload.dimensionId);
                }
              } catch (err) {
                console.error("Drop parse error:", err);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",
              minHeight: 36,
              border: mode === "dark"
                ? "1px dashed rgba(255,255,255,0.1)"
                : "1px dashed rgba(0,0,0,0.08)",
              borderRadius: 8,
              background: mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.015)",
              transition: "all 0.15s ease",
            }}
          >
            {/* Zone label */}
            <span
              style={{
                fontSize: "0.7em",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: mode === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.65)",
                minWidth: 42,
                flexShrink: 0,
              }}
            >
              Points
            </span>

            {/* Native-draggable buttons: drag out = delete, drag onto another = reorder */}
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
              {allItems.map((item) => (
                <div
                  key={itemKey(item)}
                  draggable
                  onDragStart={(e) => {
                    const payload = { type: item.type, id: item.id, dimId: item.dimId, name: item.name, kind: "relation-reorder" };
                    e.dataTransfer.setData(
                      "application/x-relation-delete",
                      JSON.stringify(payload)
                    );
                    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
                    e.dataTransfer.effectAllowed = "move";
                    (e.currentTarget as HTMLElement).style.opacity = "0.4";
                  }}
                  onDragEnd={(e) => {
                    (e.currentTarget as HTMLElement).style.opacity = "1";
                    // Dropped outside any valid drop target → delete
                    if (e.dataTransfer.dropEffect === "none" && Date.now() - lastDropRef.current > 500) {
                      removeItem(item);
                    }
                  }}
                  onDragOver={(e) => {
                    const effectAllowed = (e.dataTransfer.effectAllowed || "").toLowerCase();
                    const hasInternal = e.dataTransfer.types.includes("application/x-relation-delete")
                      || !!e.dataTransfer.getData("application/x-relation-delete")
                      || effectAllowed.includes("move");
                    if (hasInternal) {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = "move";
                      (e.currentTarget as HTMLElement).style.outline = "2px solid #4ade80";
                      (e.currentTarget as HTMLElement).style.outlineOffset = "2px";
                    }
                  }}
                  onDragLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.outline = "";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "";
                  }}
                  onDrop={(e) => {
                    (e.currentTarget as HTMLElement).style.outline = "";
                    (e.currentTarget as HTMLElement).style.outlineOffset = "";
                    lastDropRef.current = Date.now();
                    const raw = e.dataTransfer.getData("application/x-relation-delete")
                      || e.dataTransfer.getData("text/plain");
                    if (!raw) return;
                    e.dataTransfer.dropEffect = "move";
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      const dragged: FlatItem & { kind?: string } = JSON.parse(raw);
                      if (dragged.kind && dragged.kind !== "relation-reorder") return;
                      if (dragged.type === item.type && dragged.id === item.id) return;
                      reorderItem(dragged, item);
                    } catch (err) {
                      console.error("Reorder drop error:", err);
                    }
                  }}
                  style={buttonStyle}
                  title="Drag to reorder · Drag out to remove"
                >
                  {item.name}
                </div>
              ))}

              {allItems.length === 0 && (
                <span style={{ color: "rgba(128,128,128,0.5)", fontStyle: "italic", fontSize: "0.9em" }}>
                  Drag points here
                </span>
              )}
            </div>
          </div>
          {/* Validation Error Banner */}
          {errorMessage && (
            <div className="syntax-error-banner">
              {errorMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- Hauptkomponente ---------- */
export default function RelationEditor({
  relations,
  onChange,
  dimensions,
  allNames,
  isActive,
  toggle,
  userHue,
  mode,
  projectId,
  syntaxData,
}: Props) {
  // Welche Relation ist aktuell aufgeklappt?
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Aktuell gezogene Relation (für DragOverlay)
  const [activeRelId, setActiveRelId] = useState<string | null>(null);
  // Ref auf den äußeren Editor-Container (für Drag-out-Grenze)
  const editorFrameRef = useRef<HTMLDivElement | null>(null);

  const relationSyntaxMap = useMemo(() => {
    const map = new Map<string, { syntax?: string; SyntaxMessages: string[] }>();
    const arr = Array.isArray(syntaxData) ? syntaxData : [];
    arr.forEach((entry: any) => {
      const name = entry?.relationName;
      if (name) {
        map.set(name, {
          syntax: entry.syntax,
          SyntaxMessages: entry.message ? [entry.message] : [],
        });
      }
    });
    return map;
  }, [syntaxData]);

  // DnD-Sensoren für Drag mit der Maus/Touch
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  );

  // Fix: Wenn Maus schnell aus dem Fenster geht, verliert dnd-kit den mouseup-Event.
  // Wir dispatchen einen synthetischen mouseup, damit dnd-kit den Drag sauber beendet.
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.relatedTarget === null) {
        document.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
      }
    };
    window.addEventListener('mouseleave', handleMouseLeave);
    return () => window.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  /**
   * Neue Relation hinzufügen:
   * - Standardname "RelationName"
   * - Typ "connection"
   * - keine Targets initial
   */
  const handleAdd = () => {
    const newRelation: Relation = {
      id: Date.now().toString(),
      name: "Relation" + (relations.length + 1),
      type: "connection",
      targets: [],
    };
    onChange([...relations, newRelation]);
    setExpandedId(newRelation.id);

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  /**
   * Relation aktualisieren:
   * - findet Relation per id
   * - wendet Updates an
   * - bei Funktionswerten (targets: prev => next) wird diese Funktion
   *   auf das aktuelle Feld angewendet
   */
  const handleUpdate = (id: string, updates: any) => {
    onChange(
      relations.map((r) =>
        r.id === id
          ? {
            ...r,
            ...Object.fromEntries(
              Object.entries(updates).map(([k, v]) => [
                k,
                typeof v === "function"
                  ? v(r[k as keyof Relation])
                  : v,
              ])
            ),
          }
          : r
      )
    );

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  /**
   * Relation löschen:
   * - entfernt sie aus der Liste
   * - klappt sie zu, falls sie gerade expanded war
   */
  const handleDelete = (id: string) => {
    onChange(relations.filter((r) => r.id !== id));
    if (expandedId === id) setExpandedId(null);

    // Activity-Ping
    notifyProjectActivity(projectId).catch(() => { });
  };

  return (
    <div
      className="relation-editor"
      ref={editorFrameRef}
      style={{
        background: getBaseColor(2, "section", userHue, mode),
      }}
    >
      {/* Header der Relations-Section */}
      <div className="relation-editor__header" onClick={toggle}>
        <strong className="relation-editor__title">
          {isActive ? "▾" : "▸"} Relations
        </strong>
      </div>

      {/* Inhalt nur anzeigen, wenn Section aktiv ist */}
      {isActive && (
        <div
          className="relation-editor__body"
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes("application/x-zeitgeist-delete")) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move"; // For deletion/canceling, 'move' is typically used
            }
          }}
          onDrop={(e) => {
            e.preventDefault();

            // Handle internal reorder/cancel delete drops
            if (e.dataTransfer.types.includes("application/x-zeitgeist-delete")) {
              // Intentionally do nothing with the data, but preventDefault 
              // and the successful drop ensures the source doesn't run its delete logic
              return;
            }
          }}
        >
          {/* DnD-Kontext für die Relations-Liste */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={(e: DragStartEvent) => {
              setExpandedId(null);
              setActiveRelId(String(e.active.id));
            }}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              setActiveRelId(null);

              // Drag-out-to-delete: Element komplett außerhalb des Editors → löschen
              const finalRect = active.rect.current.translated ?? active.rect.current.initial;
              const editorRect = editorFrameRef.current?.getBoundingClientRect();
              if (
                finalRect && editorRect &&
                (finalRect.right <= editorRect.left ||
                  finalRect.left >= editorRect.right ||
                  finalRect.bottom <= editorRect.top ||
                  finalRect.top >= editorRect.bottom)
              ) {
                handleDelete(String(active.id));
                return;
              }

              if (over && active.id !== over.id) {
                const oldIndex = relations.findIndex((r) => r.id === active.id);
                const newIndex = relations.findIndex((r) => r.id === over.id);
                onChange(arrayMove(relations, oldIndex, newIndex));
                notifyProjectActivity(projectId).catch(() => { });
              }
            }}
          >
            <SortableContext
              items={relations.map((r) => r.id)}
              strategy={verticalListSortingStrategy}
            >
              {relations.map((relation) => (
                <SortableRelationCard
                  key={relation.id}
                  relation={relation}
                  expandedId={expandedId}
                  setExpandedId={setExpandedId}
                  onUpdate={handleUpdate}
                  onDelete={handleDelete}
                  dimensions={dimensions}
                  userHue={userHue}
                  mode={mode}
                  allNames={allNames}
                  syntaxStat={relationSyntaxMap.get(relation.name)}
                />
              ))}
            </SortableContext>

            {/* DragOverlay – Ghost-Karte beim Ziehen */}
            <DragOverlay>
              {activeRelId ? (
                <div
                  className="drag-overlay-card"
                  style={{
                    background: getBaseColor(4, relations.find(r => r.id === activeRelId)?.type ?? "connection", userHue, mode),
                  }}
                >
                  <div className="editor-card__drag">☰</div>
                  <div style={{ flex: 1 }}>
                    {relations.find(r => r.id === activeRelId)?.name ?? ""}
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {/* Button zum Anlegen einer neuen Relation */}
          <button
            onClick={handleAdd}
            className="relation-editor__add"
          >
            + Relation
          </button>
        </div>
      )}
    </div>
  );
}
